import { lazy, Suspense, type ComponentType } from "react";
import { createBrowserRouter, Navigate, RouterProvider, useLocation } from "react-router-dom";
import { AppShell } from "@/app/layouts/AppShell";
import { HybridLayoutWrapper } from "@/app/layouts/HybridLayoutWrapper";
import { PublicAppShell } from "@/app/layouts/PublicAppShell";
import { PrivateRoute } from "@/app/guards/PrivateRoute";
import { RoleGuard } from "@/app/guards/RoleGuard";
import { ErrorBoundary, Skeleton } from "@/shared/feedback";
import { useAuthStore } from "@/lib/auth/authStore";
import type { CapabilityModuleKey } from "@/features/capabilities/types";
import type { BusinessRole, ProfileRole, SystemRole } from "@/types/roles";

const LoginPage = lazy(() => import("@/pages/auth/LoginPage").then((module) => ({ default: module.LoginPage })));
const RegisterPage = lazy(() => import("@/pages/auth/RegisterPage").then((module) => ({ default: module.RegisterPage })));
const ForgotPasswordPage = lazy(() => import("@/pages/auth/ForgotPasswordPage").then((module) => ({ default: module.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import("@/pages/auth/ResetPasswordPage").then((module) => ({ default: module.ResetPasswordPage })));
const DashboardPage = lazy(() => import("@/pages/app/DashboardPage").then((module) => ({ default: module.DashboardPage })));

const AccountPage = lazy(() => import("@/features/account/pages").then((module) => ({ default: module.AccountPage })));
const AccountSecurityPage = lazy(() => import("@/features/account/pages").then((module) => ({ default: module.AccountSecurityPage })));
const AccountPrivacyPage = lazy(() => import("@/features/account/pages").then((module) => ({ default: module.AccountPrivacyPage })));
const ProfilesPage = lazy(() => import("@/features/profiles/pages").then((module) => ({ default: module.ProfilesPage })));
const QRScannerPage = lazy(() => import("@/features/qr/pages").then((module) => ({ default: module.QRScannerPage })));
const PatientHomePage = lazy(() => import("@/features/patient/pages").then((module) => ({ default: module.PatientHomePage })));
const BasicAppointmentsPage = lazy(() => import("@/features/appointments/pages").then((module) => ({ default: module.BasicAppointmentsPage })));
const BasicBookingPage = lazy(() => import("@/features/appointments/pages").then((module) => ({ default: module.BasicBookingPage })));
const PatientAppointmentsPage = lazy(() => import("@/features/appointments/pages").then((module) => ({ default: module.PatientAppointmentsPage })));
const PatientBookingPage = lazy(() => import("@/features/appointments/pages").then((module) => ({ default: module.PatientBookingPage })));
const PatientClinicalHistoryPage = lazy(() =>
  import("@/features/clinical/pages").then((module) => ({ default: module.PatientClinicalHistoryPage })),
);
const PatientMonitoringPage = lazy(() => import("@/features/monitoring/pages").then((module) => ({ default: module.PatientMonitoringPage })));
const PatientMonitoringDetailPage = lazy(() => import("@/features/monitoring/pages").then((module) => ({ default: module.PatientMonitoringDetailPage })));
const ProfessionalPage = lazy(() => import("@/features/professional/pages").then((module) => ({ default: module.ProfessionalPage })));
const ProfessionalHomePage = lazy(() => import("@/features/professional/pages").then((module) => ({ default: module.ProfessionalHomePage })));
const ProfessionalSchedulePage = lazy(() => import("@/features/appointments/pages").then((module) => ({ default: module.ProfessionalSchedulePage })));
const ProfessionalCareerPage = lazy(() => import("@/features/professional/pages").then((module) => ({ default: module.ProfessionalCareerPage })));
const ProfessionalSectionsPage = lazy(() => import("@/features/professional/pages").then((module) => ({ default: module.ProfessionalSectionsPage })));
const ProfessionalSettingsPage = lazy(() => import("@/features/professional/pages").then((module) => ({ default: module.ProfessionalSettingsPage })));
const ProfessionalPatientsReportsPage = lazy(() =>
  import("@/features/professional/pages").then((module) => ({ default: module.ProfessionalPatientsReportsPage })),
);
const ProfessionalPublicLandingPage = lazy(() =>
  import("@/features/professional/components/ProfessionalPublicLandingPage").then((module) => ({ default: module.ProfessionalPublicLandingPage })),
);
const SearchPage = lazy(() => import("@/features/search/pages").then((module) => ({ default: module.SearchPage })));
const IndexPage  = lazy(() => import("@/features/showcase/pages").then((module) => ({ default: module.IndexPage })));
const AboutPage  = lazy(() => import("@/features/about/pages").then((module) => ({ default: module.AboutPage })));
const ProfessionalClinicalWorkbenchPage = lazy(() =>
  import("@/features/clinical/pages").then((module) => ({ default: module.ProfessionalClinicalWorkbenchPage })),
);
const DoctorMonitoringPage = lazy(() => import("@/features/monitoring/pages").then((module) => ({ default: module.DoctorMonitoringPage })));
const DoctorMonitoringDetailPage = lazy(() => import("@/features/monitoring/pages").then((module) => ({ default: module.DoctorMonitoringDetailPage })));
const BusinessesPage = lazy(() => import("@/features/businesses/pages").then((module) => ({ default: module.BusinessesPage })));
const DoctorBusinessesPage = lazy(() => import("@/features/businesses/pages").then((module) => ({ default: module.DoctorBusinessesPage })));
const BusinessDetailPage = lazy(() => import("@/features/businesses/pages").then((module) => ({ default: module.BusinessDetailPage })));
const BusinessStaffPage = lazy(() => import("@/features/businesses/pages").then((module) => ({ default: module.BusinessStaffPage })));
const BusinessServicesPage = lazy(() => import("@/features/businesses/pages").then((module) => ({ default: module.BusinessServicesPage })));
const BusinessesDirectoryPage = lazy(() => import("@/features/businesses/pages").then((module) => ({ default: module.BusinessesDirectoryPage })));
const PublicBusinessProfilePage = lazy(() => import("@/features/businesses/pages").then((module) => ({ default: module.PublicBusinessProfilePage })));
const BusinessCatalogDetailPage = lazy(() => import("@/features/businesses/BusinessCatalogDetailPage").then((module) => ({ default: module.BusinessCatalogDetailPage })));
const BusinessManageOldPage = lazy(() => import("@/features/businesses/pages").then((module) => ({ default: module.BusinessManagePage })));
const BusinessManagePreviewPage = lazy(() => import("@/features/businesses/pages").then((module) => ({ default: module.BusinessManagePreviewPage })));
const BusinessRegistrationPage = lazy(() => import("@/features/businesses/pages").then((module) => ({ default: module.BusinessRegistrationPage })));
const BusinessInviteAcceptPage = lazy(() => import("@/features/businesses/pages").then((module) => ({ default: module.BusinessInviteAcceptPage })));
const BusinessBillingPage = lazy(() => import("@/features/businesses/pages").then((module) => ({ default: module.BusinessBillingPage })));
const CommercePage = lazy(() => import("@/features/medimarket/pages").then((module) => ({ default: module.CommercePage })));
const CommerceDetailPage = lazy(() => import("@/features/medimarket/components/CommerceDetailPage").then((module) => ({ default: module.CommerceDetailPage })));
const CartPage = lazy(() => import("@/features/medimarket/pages").then((module) => ({ default: module.CartPage })));
const SellerDashboardPage = lazy(() => import("@/features/medimarket/pages").then((module) => ({ default: module.SellerDashboardPage })));
const OrderHistoryPage = lazy(() => import("@/features/medimarket/pages").then((module) => ({ default: module.OrderHistoryPage })));
const WalletPage = lazy(() => import("@/features/wallet/pages").then((module) => ({ default: module.WalletPage })));
const InvoicesPage = lazy(() => import("@/features/invoices/pages").then((module) => ({ default: module.InvoicesPage })));
const PaymentsPage = lazy(() => import("@/features/payments/pages").then((module) => ({ default: module.PaymentsPage })));
const ReportsPage = lazy(() => import("@/features/blog/pages").then((module) => ({ default: module.ReportsPage })));
const AdminReviewsPage = lazy(() => import("@/features/reviews/AdminReviewsPage").then((module) => ({ default: module.AdminReviewsPage })));
const PrivacyPage = lazy(() => import("@/features/privacy/pages").then((module) => ({ default: module.PrivacyPage })));
const ManualAdInvoicePage = lazy(() => import("@/features/ads/ManualAdInvoicePage").then((module) => ({ default: module.ManualAdInvoicePage })));
const PatientFamilyPage = lazy(() => import("@/features/family-care/pages").then((module) => ({ default: module.PatientFamilyPage })));
const FamilyRequestPage = lazy(() => import("@/features/family-care/pages").then((module) => ({ default: module.FamilyRequestPage })));
const FamilyDashboardPage = lazy(() => import("@/features/family-care/pages").then((module) => ({ default: module.FamilyDashboardPage })));
const FamilyClinicalHistoryPage = lazy(() =>
  import("@/features/family-care/pages").then((module) => ({ default: module.FamilyClinicalHistoryPage })),
);
const FamilyMonitoringDetailPage = lazy(() =>
  import("@/features/family-care/pages").then((module) => ({ default: module.FamilyMonitoringDetailPage })),
);
const LaboratoryPage = lazy(() => import("@/features/laboratory/pages").then((module) => ({ default: module.LaboratoryPage })));
const ImagingPage = lazy(() => import("@/features/imaging/pages").then((module) => ({ default: module.ImagingPage })));
const LaboratoryAdminPage = lazy(() => import("@/features/laboratory/admin/LaboratoryDashboard").then((module) => ({ default: module.LaboratoryAdminPage })));
const ServiceHubPage  = lazy(() => import("@/features/service-hub/pages").then((module) => ({ default: module.ServiceHubPage })));
const InventoryPage   = lazy(() => import("@/features/inventory/pages").then((module) => ({ default: module.InventoryPage })));
const HospitalAdmissionsPage = lazy(() => import("@/features/hospital/pages").then((module) => ({ default: module.HospitalAdmissionsPage })));
const HospitalBedsPage = lazy(() => import("@/features/hospital/pages").then((module) => ({ default: module.HospitalBedsPage })));
const JobsPage        = lazy(() => import("@/features/jobs/pages").then((module) => ({ default: module.JobsPage })));
const InsurancePage   = lazy(() => import("@/features/insurance/pages").then((module) => ({ default: module.InsurancePage })));
const EventsPage      = lazy(() => import("@/features/events/pages").then((module) => ({ default: module.EventsPage })));
const EventsPreviewPage = lazy(() => import("@/features/events/EventsPreviewPage").then((module) => ({ default: module.EventsPreviewPage })));
const AdminEventReviewPage = lazy(() => import("@/features/events/AdminEventReviewPage").then((module) => ({ default: module.AdminEventReviewPage })));
const LabResultsPage = lazy(() => import("@/features/laboratory/pages").then((module) => ({ default: module.LabResultsPage })));
const MessagesPage = lazy(() => import("@/features/messages/pages").then((module) => ({ default: module.MessagesPage })));
const FavoritesPage = lazy(() => import("@/features/favorites/pages").then((module) => ({ default: module.FavoritesPage })));
const AdminPage = lazy(() => import("@/features/admin/pages").then((module) => ({ default: module.AdminPage })));
const AdminUsersPage = lazy(() => import("@/features/admin/pages").then((module) => ({ default: module.AdminUsersPage })));
const AdminOrganizationsPage = lazy(() => import("@/features/admin/pages").then((module) => ({ default: module.AdminOrganizationsPage })));
const AdminMediaPage = lazy(() => import("@/features/admin/pages").then((module) => ({ default: module.AdminMediaPage })));
const AdminAnalyticsPage = lazy(() => import("@/features/admin/pages").then((module) => ({ default: module.AdminAnalyticsPage })));
const AdminBillingPage = lazy(() => import("@/features/admin/pages").then((module) => ({ default: module.AdminBillingPage })));
const AdminSecurityPage = lazy(() => import("@/features/admin/pages").then((module) => ({ default: module.AdminSecurityPage })));
const AdminModerationPage = lazy(() => import("@/features/admin/pages").then((module) => ({ default: module.AdminModerationPage })));
const AdminPostModerationPage = lazy(() => import("@/features/admin/components/AdminPostModeration").then((module) => ({ default: module.AdminPostModeration })));
const SuperAdminPage = lazy(() => import("@/features/superadmin/pages").then((module) => ({ default: module.SuperAdminPage })));
const AdsManagementPage = lazy(() => import("@/features/ads/AdsManagementTab").then((module) => ({ default: module.AdsManagementTab })));
const HeroCarouselManagementPage = lazy(() => import("@/features/showcase/admin/HeroCarouselManagementPage").then((module) => ({ default: module.HeroCarouselManagementPage })));
const BusinessNomenclaturePage = lazy(() => import("@/features/superadmin/BusinessNomenclaturePage").then((module) => ({ default: module.BusinessNomenclaturePage })));
const ReportBuilderPage = lazy(() => import("@/features/report-builder/pages").then((module) => ({ default: module.ReportBuilderPage })));
const ReportFillPage = lazy(() => import("@/features/report-builder/ReportFillPage").then((module) => ({ default: module.ReportFillPage })));
const PricingPage         = lazy(() => import("@/features/pricing/pages").then((module) => ({ default: module.PricingPage })));
const CorporateDashboardPage = lazy(() => import("@/features/corporate/pages").then((module) => ({ default: module.CorporateDashboardPage })));
const FinancePage = lazy(() => import("@/features/finance/pages").then((module) => ({ default: module.FinancePage })));
const BusinessAnalyticsPage = lazy(() => import("@/features/analytics/BusinessAnalyticsPage").then((module) => ({ default: module.BusinessAnalyticsPage })));
const PostDetailPage = lazy(() => import("@/features/posts/PostDetailPage").then((module) => ({ default: module.PostDetailPage })));
const DashboardPreviewPage = lazy(() => import("@/features/dashboard-preview/DashboardPreviewPage").then((module) => ({ default: module.DashboardPreviewPage })));
const BusinessWorkspacePreviewPage = lazy(() => import("@/features/dashboard-preview/DashboardPreviewPage").then((module) => ({ default: module.BusinessWorkspacePreviewPage })));
const DashboardPreviewHub = lazy(() => import("@/features/dashboard-preview/DashboardPreviewPage").then((module) => ({ default: module.DashboardPreviewHub })));
const UniversalDashboardPreviewPage = lazy(() => import("@/features/dashboard-preview/DashboardPreviewPage").then((module) => ({ default: module.UniversalDashboardPreviewPage })));

interface RouteDefinition {
  path: string;
  component: ComponentType;
  systemRoles?: readonly SystemRole[];
  profileRoles?: readonly ProfileRole[];
  businessRoles?: readonly BusinessRole[];
  moduleKey?: CapabilityModuleKey;
  redirectTo?: string;
  redirectMessage?: string;
}

const appRoutes: RouteDefinition[] = [
  { path: "dashboard", component: DashboardPage },
  { path: "dashboard-preview", component: DashboardPreviewHub },
  { path: "dashboard-preview/universal", component: UniversalDashboardPreviewPage },
  { path: "dashboard-preview/patient", component: () => <DashboardPreviewPage role="patient" />, profileRoles: ["patient"] },
  { path: "dashboard-preview/doctor", component: () => <DashboardPreviewPage role="doctor" />, profileRoles: ["doctor"] },
  { path: "dashboard-preview/specialist", component: () => <DashboardPreviewPage role="specialist" />, profileRoles: ["specialist"] },
  { path: "dashboard-preview/technician", component: () => <DashboardPreviewPage role="technician" />, profileRoles: ["technician"] },
  { path: "dashboard-preview/student", component: () => <DashboardPreviewPage role="student" />, profileRoles: ["student"] },
  { path: "businesses-preview", component: BusinessWorkspacePreviewPage },
  { path: "account", component: AccountPage },
  { path: "account/security", component: AccountSecurityPage },
  { path: "account/privacy", component: AccountPrivacyPage },
  { path: "profiles", component: ProfilesPage },
  { path: "appointments", component: BasicAppointmentsPage, moduleKey: "appointments" },
  { path: "appointments/book", component: BasicBookingPage, moduleKey: "appointments" },
  { path: "patient", component: PatientHomePage, profileRoles: ["patient"] },
  { path: "patient/appointments", component: PatientAppointmentsPage, moduleKey: "appointments" },
  { path: "patient/book", component: PatientBookingPage, moduleKey: "appointments" },
  {
    path: "patient/history",
    component: PatientClinicalHistoryPage,
    profileRoles: ["patient"],
    redirectTo: "/app/admin",
    redirectMessage: "Roli administrativ nuk lejohet te hape historikun klinik te pacientit.",
  },
  { path: "patient/monitoring", component: PatientMonitoringPage, profileRoles: ["patient"], moduleKey: "monitoring" },
  { path: "patient/monitoring/:sessionId", component: PatientMonitoringDetailPage, profileRoles: ["patient"], moduleKey: "monitoring" },
  { path: "professional",      component: ProfessionalHomePage, profileRoles: ["doctor", "specialist"] },
  { path: "professional/profile", component: ProfessionalPage,     profileRoles: ["doctor", "specialist", "technician", "student"] },
  { path: "professional/schedule", component: ProfessionalSchedulePage, profileRoles: ["doctor", "specialist"], moduleKey: "appointments" },
  { path: "professional/career", component: ProfessionalCareerPage, profileRoles: ["doctor", "specialist", "technician"] },
  { path: "professional/sections", component: ProfessionalSectionsPage, profileRoles: ["doctor", "specialist", "technician"] },
  { path: "professional/settings", component: ProfessionalSettingsPage, profileRoles: ["doctor", "specialist", "technician"] },
  { path: "professional/patients", component: ProfessionalPatientsReportsPage, profileRoles: ["doctor", "specialist"] },
  { path: "professional/reports", component: ProfessionalClinicalWorkbenchPage, profileRoles: ["doctor", "specialist"] },
  { path: "professional/reports/fill/:reportId", component: ReportFillPage, profileRoles: ["doctor", "specialist"] },
  { path: "doctor/monitoring", component: DoctorMonitoringPage, profileRoles: ["doctor", "specialist"], moduleKey: "monitoring" },
  { path: "doctor/monitoring/:sessionId", component: DoctorMonitoringDetailPage, profileRoles: ["doctor", "specialist"], moduleKey: "monitoring" },
  { path: "professional/businesses", component: DoctorBusinessesPage, profileRoles: ["doctor", "specialist", "technician"] },
  { path: "businesses", component: BusinessesPage, businessRoles: ["owner", "staff", "technical", "doctor"] },
  { path: "business/register", component: BusinessRegistrationPage },
  { path: "business/invites", component: BusinessInviteAcceptPage },
  { path: "business/manage", component: BusinessManagePreviewPage, businessRoles: ["owner", "staff", "technical", "doctor"] },
  { path: "business/manage-old", component: BusinessManageOldPage, businessRoles: ["owner", "staff", "technical", "doctor"] },
  { path: "business/manage-preview", component: BusinessManagePreviewPage, businessRoles: ["owner"] },
  { path: "business/billing", component: BusinessBillingPage, businessRoles: ["owner"] },
  { path: "business/commerce", component: SellerDashboardPage, businessRoles: ["owner", "staff", "technical"] },
  { path: "businesses/:id", component: BusinessDetailPage, businessRoles: ["owner", "staff", "technical", "doctor"] },
  { path: "businesses/:id/staff", component: BusinessStaffPage, businessRoles: ["owner", "staff"] },
  { path: "businesses/:id/services", component: BusinessServicesPage, businessRoles: ["owner", "staff", "doctor"] },
  // commerce dhe search u kaluan te routes publike /search dhe /commerce
  { path: "seller",    component: SellerDashboardPage, businessRoles: ["owner", "staff", "technical"] },
  { path: "cart",      component: CartPage },
  { path: "orders",    component: OrderHistoryPage },
  { path: "favorites", component: FavoritesPage },
  { path: "wallet", component: WalletPage, moduleKey: "wallet" },
  { path: "qr", component: QRScannerPage },
  // Faturat — te gjith perdoruesit e kyqur (owner+staff+patient+mjek)
  // businessRoles u hoq: wallet/history eshte i aksesueshëm per te gjithe
  { path: "invoices", component: InvoicesPage },
  { path: "payments", component: PaymentsPage },
  { path: "blog", component: ReportsPage },
  { path: "reports", component: ProfessionalPatientsReportsPage, profileRoles: ["doctor", "specialist"] },
  { path: "privacy", component: PrivacyPage },
  { path: "patient/family", component: PatientFamilyPage, profileRoles: ["patient"], moduleKey: "family_care" },
  { path: "family/request", component: FamilyRequestPage, moduleKey: "family_care" },
  { path: "family/dashboard", component: FamilyDashboardPage, moduleKey: "family_care" },
  { path: "family/history/:patientUserId", component: FamilyClinicalHistoryPage, moduleKey: "family_care" },
  { path: "family/monitoring/:sessionId", component: FamilyMonitoringDetailPage, moduleKey: "family_care" },
  { path: "laboratory", component: LaboratoryPage, profileRoles: ["patient", "doctor", "specialist", "technician"], moduleKey: "laboratory" },
  { path: "laboratory/results", component: LabResultsPage, profileRoles: ["patient", "doctor", "specialist"], moduleKey: "laboratory" },
  { path: "laboratory/admin", component: LaboratoryAdminPage, profileRoles: ["technician"], businessRoles: ["owner", "staff", "technical"], moduleKey: "laboratory" },
  { path: "service-hub", component: ServiceHubPage, businessRoles: ["owner", "staff", "technical", "doctor"] },
  { path: "inventory",   component: InventoryPage,  businessRoles: ["owner", "staff"], moduleKey: "inventory" },
  { path: "hospital/admissions", component: HospitalAdmissionsPage, businessRoles: ["owner"] },
  { path: "hospital/beds", component: HospitalBedsPage, businessRoles: ["owner"] },
  { path: "jobs",        component: JobsPage },
  { path: "insurance",  component: InsurancePage },
  { path: "events",     component: EventsPreviewPage },
  { path: "events2",    component: EventsPage },
  { path: "imaging", component: ImagingPage, profileRoles: ["patient", "doctor", "specialist"] },
  { path: "messages", component: MessagesPage, moduleKey: "chat" },
  { path: "admin", component: AdminPage, systemRoles: ["admin", "superadmin"], moduleKey: "admin" },
  { path: "admin/users", component: AdminUsersPage, systemRoles: ["superadmin"], moduleKey: "admin" },
  { path: "admin/billing", component: AdminBillingPage, systemRoles: ["superadmin"], moduleKey: "admin" },
  { path: "admin/organizations", component: AdminOrganizationsPage, systemRoles: ["superadmin"], moduleKey: "admin" },
  { path: "admin/media", component: AdminMediaPage, systemRoles: ["superadmin"], moduleKey: "admin" },
  { path: "admin/analytics", component: AdminAnalyticsPage, systemRoles: ["superadmin"], moduleKey: "analytics" },
  { path: "admin/security", component: AdminSecurityPage, systemRoles: ["superadmin"], moduleKey: "admin" },
  { path: "admin/moderation", component: AdminModerationPage, systemRoles: ["admin", "superadmin"], moduleKey: "admin" },
  { path: "superadmin", component: SuperAdminPage, systemRoles: ["superadmin"], moduleKey: "superadmin" },
  { path: "superadmin/verify-doctors", component: AdminUsersPage, systemRoles: ["superadmin"], moduleKey: "admin" },
  { path: "superadmin/users", component: SuperAdminPage, systemRoles: ["superadmin"], moduleKey: "superadmin" },
  { path: "superadmin/profiles", component: SuperAdminPage, systemRoles: ["superadmin"], moduleKey: "superadmin" },
  { path: "superadmin/cities", component: SuperAdminPage, systemRoles: ["superadmin"], moduleKey: "superadmin" },
  { path: "superadmin/businesses", component: SuperAdminPage, systemRoles: ["superadmin"], moduleKey: "superadmin" },
  { path: "superadmin/departments", component: SuperAdminPage, systemRoles: ["superadmin"], moduleKey: "superadmin" },
  { path: "superadmin/services", component: SuperAdminPage, systemRoles: ["superadmin"], moduleKey: "superadmin" },
  { path: "superadmin/security", component: SuperAdminPage, systemRoles: ["superadmin"], moduleKey: "superadmin" },
  { path: "superadmin/analytics", component: AdminAnalyticsPage, systemRoles: ["superadmin"], moduleKey: "superadmin" },
  { path: "superadmin/billing", component: SuperAdminPage, systemRoles: ["superadmin"], moduleKey: "superadmin" },
  { path: "superadmin/posts", component: AdminPostModerationPage, systemRoles: ["superadmin"], moduleKey: "admin" },
  { path: "superadmin/events", component: AdminEventReviewPage, systemRoles: ["superadmin"], moduleKey: "superadmin" },
  { path: "superadmin/ads", component: AdsManagementPage, systemRoles: ["superadmin"], moduleKey: "superadmin" },
  { path: "superadmin/showcase-carousel", component: HeroCarouselManagementPage, systemRoles: ["superadmin"], moduleKey: "superadmin" },
  { path: "superadmin/reviews", component: AdminReviewsPage, systemRoles: ["superadmin"], moduleKey: "superadmin" },
  { path: "superadmin/business-nomenclature", component: BusinessNomenclaturePage, systemRoles: ["superadmin"], moduleKey: "superadmin" },
  { path: "superadmin/report-builder", component: ReportBuilderPage, systemRoles: ["superadmin"], moduleKey: "superadmin" },
  { path: "pricing",    component: PricingPage },
  { path: "corporate",  component: CorporateDashboardPage, businessRoles: ["owner"] },
  { path: "finance",    component: FinancePage,            businessRoles: ["owner"], moduleKey: "finance" },
  { path: "analytics",  component: BusinessAnalyticsPage,  businessRoles: ["owner"], moduleKey: "analytics" },
];

const renderRoute = (route: RouteDefinition): JSX.Element => {
  const Component = route.component;

  return (
    <ErrorBoundary>
      <Suspense fallback={<RouteSkeleton />}>
        <RoleGuard
          systemRoles={route.systemRoles}
          profileRoles={route.profileRoles}
          businessRoles={route.businessRoles}
          moduleKey={route.moduleKey}
          redirectTo={route.redirectTo}
          redirectMessage={route.redirectMessage}
        >
          <Component />
        </RoleGuard>
      </Suspense>
    </ErrorBoundary>
  );
};

const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <Suspense fallback={<RouteSkeleton />}>
        <ShowcaseRoot />
      </Suspense>
    ),
  },
  {
    path: "/login",
    element: (
      <Suspense fallback={<RouteSkeleton />}>
        <LoginPage />
      </Suspense>
    ),
  },
  {
    path: "/register",
    element: (
      <Suspense fallback={<RouteSkeleton />}>
        <RegisterPage />
      </Suspense>
    ),
  },
  {
    path: "/forgot-password",
    element: (
      <Suspense fallback={<RouteSkeleton />}>
        <ForgotPasswordPage />
      </Suspense>
    ),
  },
  {
    path: "/reset-password",
    element: (
      <Suspense fallback={<RouteSkeleton />}>
        <ResetPasswordPage />
      </Suspense>
    ),
  },
  {
    path: "/privacy",
    element: (
      <Suspense fallback={<RouteSkeleton />}>
        <PublicAppShell><PrivacyPage /></PublicAppShell>
      </Suspense>
    ),
  },
  {
    path: "/invoice/manual/:token",
    element: (
      <Suspense fallback={<RouteSkeleton />}>
        <PublicAppShell><ManualAdInvoicePage /></PublicAppShell>
      </Suspense>
    ),
  },
  {
    path: "/app/privacy",
    element: (
      <Suspense fallback={<RouteSkeleton />}>
        <PublicAppShell><PrivacyPage /></PublicAppShell>
      </Suspense>
    ),
  },
  {
    path: "/businesses",
    element: (
      <Suspense fallback={<RouteSkeleton />}>
        <HybridLayoutWrapper><BusinessesDirectoryPage /></HybridLayoutWrapper>
      </Suspense>
    ),
  },
  {
    path: "/businesses/:slug/departments/:departmentId",
    element: (
      <Suspense fallback={<RouteSkeleton />}>
        <HybridLayoutWrapper><BusinessCatalogDetailPage /></HybridLayoutWrapper>
      </Suspense>
    ),
  },
  {
    path: "/businesses/:slug/services/:serviceId",
    element: (
      <Suspense fallback={<RouteSkeleton />}>
        <HybridLayoutWrapper><BusinessCatalogDetailPage /></HybridLayoutWrapper>
      </Suspense>
    ),
  },
  {
    path: "/businesses/:slug",
    element: (
      <Suspense fallback={<RouteSkeleton />}>
        <HybridLayoutWrapper><PublicBusinessProfilePage /></HybridLayoutWrapper>
      </Suspense>
    ),
  },
  {
    path: "/doctor/:slug",
    element: (
      <Suspense fallback={<RouteSkeleton />}>
        <PublicAppShell><ProfessionalPublicLandingPage /></PublicAppShell>
      </Suspense>
    ),
  },
  {
    path: "/about",
    element: (
      <Suspense fallback={<RouteSkeleton />}>
        <PublicAppShell><AboutPage /></PublicAppShell>
      </Suspense>
    ),
  },
  {
    path: "/search",
    element: (
      <Suspense fallback={<RouteSkeleton />}>
        <HybridLayoutWrapper><SearchPage /></HybridLayoutWrapper>
      </Suspense>
    ),
  },
  {
    path: "/commerce",
    element: (
      <Suspense fallback={<RouteSkeleton />}>
        <HybridLayoutWrapper><CommercePage /></HybridLayoutWrapper>
      </Suspense>
    ),
  },
  {
    path: "/medimarket",
    element: (
      <Suspense fallback={<RouteSkeleton />}>
        <HybridLayoutWrapper><CommercePage /></HybridLayoutWrapper>
      </Suspense>
    ),
  },
  {
    path: "/commerce/product/:id",
    element: (
      <Suspense fallback={<RouteSkeleton />}>
        <HybridLayoutWrapper><CommerceDetailPage /></HybridLayoutWrapper>
      </Suspense>
    ),
  },
  {
    path: "/posts/:postId",
    element: (
      <Suspense fallback={<RouteSkeleton />}>
        <PostDetailPage />
      </Suspense>
    ),
  },
  {
    path: "/app/search",
    element: (
      <PrivateRoute>
        <RouteAlias to="/search" />
      </PrivateRoute>
    ),
  },
  {
    path: "/app/commerce",
    element: (
      <PrivateRoute>
        <RouteAlias to="/commerce" />
      </PrivateRoute>
    ),
  },
  {
    path: "/appointments/book",
    element: (
      <PrivateRoute>
        <RouteAlias to="/app/appointments/book" />
      </PrivateRoute>
    ),
  },
  {
    path: "/doctor/dashboard",
    element: (
      <PrivateRoute>
        <RouteAlias to="/app/professional" />
      </PrivateRoute>
    ),
  },
  {
    path: "/app",
    element: (
      <PrivateRoute>
        <AppShell />
      </PrivateRoute>
    ),
    children: [
      { index: true, element: <Navigate to="/app/dashboard" replace /> },
      ...appRoutes.map((route) => ({
        path: route.path,
        element: renderRoute(route),
      })),
    ],
  },
  {
    path: "*",
    element: <Navigate to="/app" replace />,
  },
]);

/**
 * Provider i router-it kryesor te React SPA.
 */
export const AppRouter = (): JSX.Element => (
  <RouterProvider router={router} future={{ v7_startTransition: true }} />
);

function RouteAlias({ to }: { to: string }): JSX.Element {
  const location = useLocation();
  const target = `${to}${location.search ?? ""}${location.hash ?? ""}`;
  return <Navigate to={target} replace />;
}

function ShowcaseRoot(): JSX.Element {
  const { accessToken, user } = useAuthStore();
  const page = <IndexPage />;
  return accessToken && user ? <AppShell>{page}</AppShell> : page;
}

function RouteSkeleton(): JSX.Element {
  return (
    <div className="space-y-4 p-6">
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-40 w-full" />
    </div>
  );
}
